import * as React from "react"
import AppBar from "@mui/material/AppBar"
import Box from "@mui/material/Box"
import Divider from "@mui/material/Divider"
import Drawer from "@mui/material/Drawer"
import IconButton from "@mui/material/IconButton"
import List from "@mui/material/List"
import ListItem from "@mui/material/ListItem"
import ListItemButton from "@mui/material/ListItemButton"
import ListItemText from "@mui/material/ListItemText"
import MenuIcon from "@mui/icons-material/Menu"
import Toolbar from "@mui/material/Toolbar"
import Typography from "@mui/material/Typography"
import Button from "@mui/material/Button"
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom"
import Footer from "./Footer"
import { Avatar, Menu, MenuItem, ListItemIcon } from "@mui/material"
import HomeIcon from "@mui/icons-material/Home"
import SettingsIcon from "@mui/icons-material/Settings"
import Logout from "@mui/icons-material/Logout"
import Person from "@mui/icons-material/Person"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import { useSelector } from "react-redux"
import Tooltip from "@mui/material/Tooltip"
import { useEffect, useState } from "react"
import MuiDialog from "./mui/MuiDialog"
import apiClient, { logoutFromServer } from "../api/apiClient"

const drawerWidth = 240

function Navbar(props) {
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const { window, type } = props
  const { isLoggedIn, userType } = useSelector((state) => state.auth)

  // Redux & Router
  const navigate = useNavigate()
  const location = useLocation()

  // State
  const [showLogoutModal, setShowLogoutModal] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const [refresh, setRefresh] = useState(true)
  const [user, setUser] = useState()

  // --- New Dropdown States ---
  const [anchorElUser, setAnchorElUser] = useState(null) // For Profile Menu
  const [anchorElNavDropdown, setAnchorElNavDropdown] = useState(null) // For Nav Dropdown

  // --- Profile Menu Handlers ---
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget)
  }
  const handleCloseUserMenu = () => {
    setAnchorElUser(null)
  }

  // --- Nav Dropdown Handlers ---
  const handleOpenNavDropdown = (event) => {
    setAnchorElNavDropdown(event.currentTarget)
  }
  const handleCloseNavDropdown = () => {
    setAnchorElNavDropdown(null)
  }

  const handleLogout = async () => {
    if (loggingOut) return
    setLoggingOut(true)
    try {
      if (await logoutFromServer()) navigate("/", { replace: true })
    } finally {
      setLoggingOut(false)
    }
  }

  const fetchProfile = async () => {
    const result = await apiClient.get("/auth/user-info")
    setUser(result)
    setRefresh(false)
  }

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState)
  }

  useEffect(() => {
    refresh && fetchProfile()
  }, [refresh])

  useEffect(() => {
    if (isLoggedIn) navigation(userType)
  }, [])

  const navigation = (role) => {
    if (role === "AD" || role === "SA") {
      !location.pathname.includes("admin") && navigate("/admin/dashboard")
    } else if (["PPTN", "TN", "EXP"].includes(role)) {
      !location.pathname.includes("tn") && navigate("/tn/dashboard")
    }
  }

  // --- Define Navigation Items (With Dropdown Support) ---
  let navItems = []

  if (type === "client") {
    navItems = [
      { label: "Dashboard", link: "/tn/dashboard" },
      { label: "Barcode", link: "/tn/barcode" },
      { label: "Samples", link: "/tn/samples" },
      { label: "Submitted Reports", link: "/tn/submitted-reports" },
    ]
  } else {
    navItems = [
      { label: "Dashboard", link: "/admin/dashboard" },
      { label: "Barcode", link: "/admin/barcode" },
      { label: "Client", link: "/admin/client" },
      { label: "Submitted Reports", link: "/admin/reports" },
      { label: "Analytics", link: "/admin/analytics" },
      // Example: Grouping extra admin items into a dropdown called "Management"


      {
        label: "Management", // Parent Label
        isDropdown: true,
        children: [

          ...(userType === "AD" ?
            [

              { label: "Sub-Admin", link: "/admin/sub-admin" },
              { label: "Reports Data", link: "/admin/report-settings" }
            ] : []),
          { label: "Technicians", link: "/admin/technicians" },
          { label: "Flagged Customers", link: "/admin/flagged-customers" },
        ],
      },


    ]
  }

  // Mobile Drawer Content
  const drawer = (
    <Box
      className="h-100"
      onClick={handleDrawerToggle}
      sx={{ textAlign: "center", backgroundColor: "#3E4755" }}
    >
      <Box className="d-flex align-item-center px-1 py-2">
        <div className="d-xs-block d-sm-none " style={{ width: "9rem" }}>
          <img width={"100%"} src={require("../assets/logo-new.png")} alt="" />
        </div>
      </Box>
      <Divider />
      <List>
        {navItems.map((item, i) => (
          <React.Fragment key={i}>
            {!item.isDropdown ? (
              // Standard Mobile Link
              <ListItem component={NavLink} to={item.link} sx={{ color: "#fff" }}>
                <ListItemButton>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ) : (
              // Mobile Dropdown Items (Rendered flat for mobile usually)
              item.children.map((child, index) => (
                <ListItem key={index} component={NavLink} to={child.link} sx={{ color: "#fff" }}>
                  <ListItemButton>
                    <ListItemText primary={`— ${child.label}`} />
                  </ListItemButton>
                </ListItem>
              ))
            )}
          </React.Fragment>
        ))}
      </List>
    </Box>
  )

  const container = window !== undefined ? () => window().document.body : undefined

  return (
    <>
      <Box className="">
        <AppBar
          component="nav"
          sx={{
            position: "static",
            xs: "100%",
            md: "96%",
            backgroundColor: "#3E4755",
            width: { xs: "100%", md: "96%" },
            margin: { xs: 0, md: "16px auto 24px" },
            borderRadius: { xs: 0, md: "16px" },
            boxShadow: "0 12px 28px rgba(15, 23, 42, 0.22)",
            border: { xs: "none", md: "1px solid rgba(255,255,255,0.08)" },
          }}
        >
          <Toolbar sx={{ minHeight: { xs: 72, md: 86 }, px: { xs: 2, md: 3 } }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: "none" } }}
            >
              <MenuIcon />
            </IconButton>

            <div className="d-none d-sm-block" style={{ width: "10rem", flexShrink: 0 }}>
              <img width={"100%"} src={require("../assets/logo-new.png")} alt="" />
            </div>

            {/* --- Desktop Navigation --- */}
            <Box
              className="w-100"
              sx={{
                display: { xs: "none", sm: "flex" },
                gap: 1.5,
                alignItems: "center",
                pl: { sm: 3, md: 5 }
              }}>
              {navItems.map((item, index) => {
                // RENDER DROPDOWN ITEM
                if (item.isDropdown) {
                  return (
                    <Box key={index}>
                      <Button
                        onClick={handleOpenNavDropdown}
                        variant="contained"
                        endIcon={<KeyboardArrowDownIcon />}
                        sx={{
                          minHeight: 42,
                          px: 2,
                          borderRadius: "8px",
                          fontSize: "0.78rem",
                          fontWeight: 500,
                          textTransform: "none",
                          letterSpacing: 0,
                          color: "#fff",
                          backgroundColor: "rgba(255,255,255,0.04)",
                          boxShadow: "0 6px 14px rgba(15, 23, 42, 0.18)",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.1)",
                            transform: "translateY(-1px)",
                            boxShadow: "0 8px 18px rgba(15, 23, 42, 0.22)"
                          },
                          transition: "all 160ms ease"
                        }}>
                        {item.label}
                      </Button>
                      <Menu
                        anchorEl={anchorElNavDropdown}
                        open={Boolean(anchorElNavDropdown)}
                        onClose={handleCloseNavDropdown}
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        transformOrigin={{ vertical: "top", horizontal: "left" }}
                        PaperProps={{
                          elevation: 0,
                          sx: {
                            mt: 1.2,
                            minWidth: 260,
                            borderRadius: "10px",
                            overflow: "hidden",
                            border: "1px solid #e7eaf0",
                            boxShadow: "0 18px 42px rgba(15, 23, 42, 0.18)",
                            "& .MuiList-root": {
                              py: 0.75
                            },
                            "& .MuiMenuItem-root": {
                              mx: 0.75,
                              my: 0.25,
                              px: 2,
                              py: 1.2,
                              borderRadius: "8px",
                              fontSize: "0.98rem",
                              fontWeight: 500,
                              color: "#2f3744",
                              transition: "background-color 160ms ease, color 160ms ease, transform 160ms ease",
                              "&:hover": {
                                backgroundColor: "#f1f4f8",
                                color: "#1f2937",
                                transform: "translateX(2px)"
                              }
                            }
                          }
                        }}
                      >
                        {item.children.map((child, childIndex) => (
                          <MenuItem
                            key={childIndex}
                            onClick={() => {
                              handleCloseNavDropdown()
                              navigate(child.link)
                            }}
                          >
                            {child.label}
                          </MenuItem>
                        ))}
                      </Menu>
                    </Box>
                  )
                }

                // RENDER STANDARD ITEM
                return (
                  <NavLink key={index} to={item.link} style={{ textDecoration: "none" }}>
                    <Button
                      className="white_space"
                      variant="contained"
                      startIcon={item.label === "Dashboard" ? <HomeIcon /> : null}
                      sx={{
                        minHeight: 42,
                        px: 2,
                        borderRadius: "8px",
                        fontSize: "0.78rem",
                        fontWeight: 500,
                        textTransform: "none",
                        letterSpacing: 0,
                        color: location.pathname === item.link ? "#121826" : "#fff",
                        backgroundColor: location.pathname === item.link ? "#fff" : "#3E4755",
                        boxShadow:
                          location.pathname === item.link
                            ? "0 8px 18px rgba(255,255,255,0.12)"
                            : "0 6px 14px rgba(15, 23, 42, 0.18)",
                        "&:hover": {
                          backgroundColor:
                            location.pathname === item.link ? "#fff" : "rgba(255,255,255,0.1)",
                          transform: "translateY(-1px)",
                          boxShadow: "0 8px 18px rgba(15, 23, 42, 0.22)"
                        },
                        transition: "all 160ms ease"
                      }}>
                      {item.label}
                    </Button>
                  </NavLink>
                )
              })}

              {/* --- Right Side Profile Dropdown --- */}
              <Box className="d-flex justify-content-end w-100 align-items-center gap-2">
                <Tooltip title="Account settings">
                  <IconButton
                    onClick={handleOpenUserMenu}
                    sx={{
                      p: 0.5,
                      borderRadius: "999px",
                      border: "1px solid rgba(255,255,255,0.28)",
                      backgroundColor: "rgba(255,255,255,0.08)",
                      transition: "all 180ms ease",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.18)",
                        transform: "translateY(-1px)"
                      }
                    }}>
                    <Avatar
                      alt="User"
                      src={user?.data.profile}
                      sx={{
                        width: 42,
                        height: 42,
                        border: "2px solid rgba(255,255,255,0.72)",
                        boxShadow: "0 8px 18px rgba(0,0,0,0.18)"
                      }}
                    />
                  </IconButton>
                </Tooltip>

                <Menu
                  sx={{ mt: 1.4 }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  keepMounted
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      minWidth: 220,
                      borderRadius: "10px",
                      overflow: "hidden",
                      border: "1px solid #e7eaf0",
                      boxShadow: "0 18px 42px rgba(15, 23, 42, 0.18)",
                      "& .MuiList-root": {
                        py: 0.75
                      },
                      "& .MuiMenuItem-root": {
                        mx: 0.75,
                        my: 0.25,
                        px: 1.5,
                        py: 1.15,
                        borderRadius: "8px",
                        fontSize: "0.98rem",
                        fontWeight: 500,
                        color: "#2f3744",
                        transition: "background-color 160ms ease, color 160ms ease, transform 160ms ease",
                        "&:hover": {
                          backgroundColor: "#f1f4f8",
                          color: "#1f2937",
                          transform: "translateX(2px)"
                        }
                      },
                      "& .MuiListItemIcon-root": {
                        minWidth: 38,
                        color: "#68717d"
                      }
                    }
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      handleCloseUserMenu()
                      navigate(type !== "client" ? "/admin/profile" : "/tn/profile")
                    }}
                  >
                    <ListItemIcon>
                      <Person fontSize="small" />
                    </ListItemIcon>
                    Profile
                  </MenuItem>

                  {!["TN", "PPTN"].includes(userType) && (
                    <MenuItem
                      onClick={() => {
                        handleCloseUserMenu()
                        navigate("/admin/settings")
                      }}
                    >
                      <ListItemIcon>
                        <SettingsIcon fontSize="small" />
                      </ListItemIcon>
                      Settings
                    </MenuItem>
                  )}

                  <Divider sx={{ my: 0.75, borderColor: "#edf0f4" }} />

                  <MenuItem
                    onClick={() => {
                      handleCloseUserMenu()
                      setShowLogoutModal(true)
                    }}
                  >
                    <ListItemIcon>
                      <Logout fontSize="small" />
                    </ListItemIcon>
                    Logout
                  </MenuItem>
                </Menu>
              </Box>

              {/* Logout Confirmation Dialog */}
              <MuiDialog
                title={"Are you sure want to Logout?"}
                show={showLogoutModal}
                Buttons={() => (
                  <>
                    <Button disabled={loggingOut} onClick={() => setShowLogoutModal(false)}>Cancel</Button>
                    <Button disabled={loggingOut} onClick={handleLogout} color="error" variant="contained">
                      {loggingOut ? "Logging out…" : "Logout"}
                    </Button>
                  </>
                )}
              />
            </Box>
          </Toolbar>
        </AppBar>

        <Box component="nav">
          <Drawer
            container={container}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: "block", sm: "none" },
              "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
            }}
          >
            {drawer}
          </Drawer>
        </Box>
      </Box>

      <Box className="app-shell" sx={{ marginTop: 8, minHeight: "100vh" }}>
        <Outlet context={{ type, setRefresh, user }} />
      </Box>

      <div className="mt-4">
        <Footer />
      </div>
    </>
  )
}

export default Navbar
